import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Instagram } from "lucide-react";
import { useSite } from "../context/SiteContext";

const NAV = [
    { label: "Ana səhifə", to: "/" },
    { label: "Kurslarımız", to: "/kurslar" },
    { label: "Müəllimlər", to: "/muellimler" },
    { label: "Psixoloq", to: "/psixoloq" },
    { label: "Yay proqramı", to: "/yay-proqrami" },
    { label: "Qalereya", to: "/qalereya" },
    { label: "Rəylər", to: "/reyler" },
    { label: "Əlaqə", to: "/elaqe" },
];

const TikTokIcon = ({ size = 16 }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
    >
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z" />
    </svg>
);

export const Header = () => {
    const { content } = useSite();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const social = content.social || {};

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo(0, 0);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-b border-neutral-200/80"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between h-20">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center gap-3 group min-w-0"
                >
                    <div className="h-11 w-11 shrink-0 flex items-center justify-center bg-white rounded-xl border border-[#0A1F3D]/10 transition-transform group-hover:scale-105">
                        <span className="font-serif-display text-[34px] leading-none text-[#0A1F3D] -mt-1">
                            K
                        </span>
                    </div>
                    <div className="leading-tight min-w-0">
                        <div className="font-serif-display text-base sm:text-lg text-[#0A1F3D] truncate">
                            KA Academy
                        </div>
                        <div className="text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-[#14545E] truncate">
                            {content.branding?.tagline}
                        </div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-6">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}
                            className={({ isActive }) =>
                                `ka-link text-[13px] text-neutral-700 hover:text-[#14545E] ${
                                    isActive ? "text-[#14545E] active font-medium" : ""
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {/* Social */}
                    <div className="hidden md:flex items-center gap-1 mr-1">
                        {social.instagram && (
                            <a
                                href={social.instagram}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="social-instagram"
                                className="p-2 text-[#0A1F3D] hover:text-[#14545E] transition-colors"
                                aria-label="Instagram"
                            >
                                <Instagram size={16} />
                            </a>
                        )}
                        {social.tiktok && (
                            <a
                                href={social.tiktok}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="social-tiktok"
                                className="p-2 text-[#0A1F3D] hover:text-[#14545E] transition-colors"
                                aria-label="TikTok"
                            >
                                <TikTokIcon size={16} />
                            </a>
                        )}
                    </div>

                    <a
                        href={`tel:${content.contact?.phone}`}
                        data-testid="header-phone"
                        className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1F3D] text-white hover:bg-[#14545E] transition-colors text-[13px]"
                    >
                        <Phone size={14} />
                        <span className="tabular">{content.contact?.phone}</span>
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="xl:hidden p-2 text-[#0A1F3D]"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="xl:hidden bg-white border-t border-neutral-200 overflow-hidden"
                    >
                        <div className="px-5 py-6 flex flex-col gap-4">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    data-testid={`mobile-nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}
                                    className={({ isActive }) =>
                                        `text-[#0A1F3D] tracking-wider text-sm ${
                                            isActive
                                                ? "text-[#14545E] font-semibold"
                                                : ""
                                        }`
                                    }
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                            <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
                                {social.instagram && (
                                    <a
                                        href={social.instagram}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="p-2 text-[#0A1F3D]"
                                        aria-label="Instagram"
                                    >
                                        <Instagram size={18} />
                                    </a>
                                )}
                                {social.tiktok && (
                                    <a
                                        href={social.tiktok}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="p-2 text-[#0A1F3D]"
                                        aria-label="TikTok"
                                    >
                                        <TikTokIcon size={18} />
                                    </a>
                                )}
                            </div>
                            <a
                                href={`tel:${content.contact?.phone}`}
                                className="px-4 py-3 rounded-full bg-[#0A1F3D] text-white text-center"
                            >
                                {content.contact?.phone}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
